// Small DOM helpers shared by the screens. No framework: elements are built once and updated in place.

type Attrs = Record<string, string | number | boolean | undefined>;
type Child = Node | string | null | undefined | false;

/** h('button.primary', { type: 'button', 'aria-label': 'Close' }, 'Close') */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs, ...children: Child[]): HTMLElementTagNameMap[K];
export function h(tag: string, attrs?: Attrs, ...children: Child[]): HTMLElement;
export function h(tag: string, attrs: Attrs = {}, ...children: Child[]): HTMLElement {
  const [name, ...classes] = tag.split('.');
  const el = document.createElement(name || 'div');
  if (classes.length) el.className = classes.join(' ');
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k === 'class') el.className = (el.className ? `${el.className} ` : '') + String(v);
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, String(v));
  }
  append(el, ...children);
  return el;
}

export function append(el: HTMLElement, ...children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
}

export function clear(el: HTMLElement): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** A button with a visible label; icon-only buttons pass `ariaLabel`. Every button is a `type="button"`. */
export function button(label: string, onClick: () => void, opts: { cls?: string; ariaLabel?: string; disabled?: boolean } = {}): HTMLButtonElement {
  const b = h('button', { type: 'button', class: opts.cls, 'aria-label': opts.ariaLabel, disabled: opts.disabled }, label);
  b.addEventListener('click', onClick);
  return b;
}

/**
 * A photo with a text fallback: if the WebP cannot load (missing file, blocked, offline cache miss) the hero
 * shows the "modelled on" name instead of a broken image (spec §5: asset failed → text placeholder).
 */
export function photo(src: string, alt: string, fallbackText: string, cls = 'photo'): HTMLElement {
  const wrap = h(`div.${cls}`);
  const img = h('img', { src, alt, loading: 'lazy', decoding: 'async' });
  img.addEventListener('error', () => {
    clear(wrap);
    wrap.classList.add('photo-missing');
    wrap.appendChild(h('span.photo-fallback', {}, fallbackText));
  });
  wrap.appendChild(img);
  return wrap;
}

export function link(label: string, href: string): HTMLAnchorElement {
  return h('a', { href, target: '_blank', rel: 'noopener noreferrer' }, label);
}

/** A labelled value cell for instrument strips and spec cards. */
export function cell(label: string, value: string, cls = 'cell'): { root: HTMLElement; value: HTMLElement; label: HTMLElement } {
  const v = h('span.cell-value', {}, value);
  const l = h('span.cell-label', {}, label);
  const root = h(`div.${cls}`, {}, l, v);
  return { root, value: v, label: l };
}

/** Styled confirm dialog (uses the <dialog id="confirm"> in index.html). Resolves true on confirm. */
export function confirmDialog(title: string, detail: string, confirmLabel: string): Promise<boolean> {
  const dlg = document.getElementById('confirm') as HTMLDialogElement | null;
  if (!dlg || typeof dlg.showModal !== 'function') return Promise.resolve(window.confirm(`${title}\n\n${detail}`));
  return new Promise((resolve) => {
    clear(dlg);
    const done = (ok: boolean): void => { dlg.close(); resolve(ok); };
    const cancel = button('Cancel', () => done(false), { cls: 'btn' });
    const ok = button(confirmLabel, () => done(true), { cls: 'btn btn-danger' });
    dlg.append(
      h('h2.dialog-title', { id: 'confirm-title' }, title),
      h('p.dialog-detail', {}, detail),
      h('div.dialog-actions', {}, cancel, ok),
    );
    dlg.addEventListener('cancel', () => resolve(false), { once: true });
    dlg.showModal();
    cancel.focus();
  });
}
